/** @jsxImportSource @emotion/react */
import * as React from 'react'
import { css } from '@emotion/react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { Alert, Box, Grid, Skeleton, Theme, useTheme } from '@mui/material'

import { Banner } from '@app/components/Guide/ui/Banner'
import { RenderSwitch } from '@app/components/Guide/ui/RenderSwitch'
import GuideBreadcrumbs from '@app/components/Guide/ui/GuideBreadcrumbs'
import ArticleCardSkeleton from '@app/components/Guide/article/ArticleCardSkeleton'

import { appLink } from '@app/config/appLink'
import { ERROR_403_MESSAGE } from '@app/app-constants'
import { useSubTabBySlug } from '@app/hooks/useStrapiQueries'
import { Tutorial } from '@app/components/Guide/types/guide.types'
import { fetchDemosBySubTabSlug, fetchTutorialsBySubTabSlug } from '@app/services/strapiServices'

const pageBg = ({ theme }: { theme: Theme }) => css`
  width: 100%;
  height: 100%;
  padding: ${theme.spacing()} ${theme.spacing(20)};

  ${theme.breakpoints.down('md')} {
    padding: ${theme.spacing(0)} ${theme.spacing(4)};
  }
`
const MainTopicPage: React.FC = () => {
  // TODO: move all the fetching to the hook folder
  const theme = useTheme()
  const navigate = useNavigate()
  const { mainTopic, subTopic } = useParams()

  const {
    data: demo,
    isLoading: isLoadingDemo,
    isError: isErrorDemo,
  } = useQuery({
    queryKey: ['demo-subtopic', subTopic],
    queryFn: () => fetchDemosBySubTabSlug(),
    enabled: mainTopic === 'demos',
  })

  const {
    data: tutorials,
    isLoading: isLoadingTutorials,
    error: errorTutorials,
    isError: isErrorTutorials,
  } = useQuery<Tutorial[]>({
    queryKey: ['tutorials-subtopic', subTopic],
    queryFn: () => fetchTutorialsBySubTabSlug(subTopic as string),
    enabled: mainTopic === 'tutorials' && Boolean(subTopic),
  })

  const slug = subTopic ?? ''

  const { data: subTab, isLoading, isError, error } = useSubTabBySlug(slug)

  if (error?.message === ERROR_403_MESSAGE || errorTutorials?.message === ERROR_403_MESSAGE) {
    navigate(appLink.homepage.path)
  }

  if (isLoading || isLoadingDemo || isLoadingTutorials) {
    return (
      <Box>
        <GuideBreadcrumbs links={[{ label: '' }]} />
        <Skeleton variant="rectangular" height={180} sx={{ my: 2 }} />
        <Box css={pageBg({ theme })}>
          <Box sx={{ px: 3, pt: 3 }}>
            <Grid container spacing={2}>
              {Array.from({ length: 3 }).map((_, i) => {
                return (
                  <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                    <ArticleCardSkeleton />
                  </Grid>
                )
              })}
            </Grid>
          </Box>
        </Box>
      </Box>
    )
  }

  if (isError || !subTab || isErrorDemo || isErrorTutorials) {
    return (
      <Box>
        <GuideBreadcrumbs links={[{ label: '' }]} />
        <Alert severity="error" sx={{ my: 2 }}>
          {error instanceof Error ? error.message : 'Something went wrong.'}
        </Alert>
      </Box>
    )
  }

  const articles = (subTab.articles ?? []).map((article) => ({
    title: article.title,
    description: article.description || '',
    path: `/app/guide/${subTab.main_topic?.slug ?? ''}/${subTab.slug ?? ''}/${article.slug}`,
    image: article.hero_image?.url ?? '',
    is_external: article.is_external ?? false,
    external_link: article.external_link ?? '',
  }))

  const breadcrumbs = [{ label: subTab?.main_topic?.title }, { label: subTab.title ?? '' }]

  const mainTopicSlug = subTab.main_topic?.slug ?? ''

  return (
    <Box css={() => pageBg({ theme })}>
      <GuideBreadcrumbs links={breadcrumbs} />
      <Box
        css={css`
          width: 100%;
          max-width: 70rem;
        `}
      >
        <Banner
          header={subTab.main_topic?.title}
          subheader={subTab.main_topic?.description || ''}
        />
      </Box>

      <Box>
        <Box
          css={css`
            font-size: 16px;
            font-weight: 700;
            line-height: 1.2;
            letter-spacing: 0.05rem;
            text-align: left;
            padding-bottom: 16px;
            color: #0b2948;
          `}
        >
          {subTab.title}
        </Box>
        <RenderSwitch
          subTopicSlug={mainTopicSlug}
          articles={articles}
          demos={demo}
          tutorials={tutorials}
        />
      </Box>
    </Box>
  )
}

export default MainTopicPage
